import type { IPhoneNumber } from '@booking-portal/types';
import { useLocalFormatter } from '@booking-portal/utils/format-utils';
import {
  email as emailRule,
  required as requiredRule,
} from '@vee-validate/rules';
import dayjs from 'dayjs';
import { isArray, isObject } from 'lodash-es';
import { useI18n } from 'vue-i18n';

export function useValidation() {
  const { t } = useI18n();
  const { formatDate } = useLocalFormatter();

  function required(value: unknown) {
    const result = requiredRule(value);
    if (result === true) {
      return true;
    }
    return t('formValidation.required');
  }

  function email(value: unknown) {
    // Empty values should be handled by the required rule
    if (!value) {
      return true;
    }
    const result = emailRule(value);
    if (result === true) {
      return true;
    }
    return t('formValidation.email');
  }

  function minArrayLength(value: unknown[], [limit]: [number]) {
    if (!value || !value.length) {
      return true;
    }

    if (value.length < limit) {
      return t('formValidation.minArrayLength', { length: limit });
    }
    return true;
  }

  function maxArrayLength(value: unknown[], [limit]: [number]) {
    if (!value || !value.length) {
      return true;
    }

    if (value.length > limit) {
      return t('formValidation.maxArrayLength', { length: limit });
    }
    return true;
  }

  function emailArray(value: string | string[]) {
    if (value && isArray(value)) {
      for (const item of value) {
        // Use the original email rule which returns boolean
        if (!emailRule(item)) {
          return t('formValidation.email');
        }
      }
    }
    return true;
  }

  function phoneNumber(value: undefined | null | string | IPhoneNumber) {
    if (!isObject(value)) {
      return true;
    }

    const trimmed = (value?.number || '').trim();

    // Skip check if no phone number was entered
    if (!trimmed) {
      return t('formValidation.required');
    }

    // Phone number needs to be at least 4 chars long
    if (!trimmed || trimmed.length < 3) {
      return t('formValidation.phone');
      // If number is valid check if a prefix is available
    } else if (!value?.prefix || !(value.prefix || '').startsWith('+')) {
      return t('formValidation.phonePrefix');
    }

    return true;
  }

  function phoneMaxLength(
    value: undefined | null | string | IPhoneNumber,
    params: [string | number]
  ) {
    if (!isObject(value)) {
      return true;
    }

    const trimmed = (value?.number || '').trim();

    // Skip check if no phone number was entered - let required handle this
    if (!trimmed) {
      return true;
    }

    const maxLen = Number(params[0]);
    if (trimmed.length > maxLen) {
      return t('formValidation.maxLength');
    }

    return true;
  }

  function maxLength(value: unknown, [limit]: [string | number]) {
    if (!value || typeof value !== 'string') {
      return true;
    }

    const maxLen = Number(limit);
    if (value.length > maxLen) {
      return t('formValidation.textMaxLength', { length: maxLen });
    }

    return true;
  }

  function minDate(value: string | Date, [referenceDate]: [Date | string]) {
    if (referenceDate && value) {
      const valueObj = dayjs(value);
      const referenceDateObj = dayjs(referenceDate);

      if (valueObj.isValid() && referenceDateObj.isValid()) {
        if (valueObj.unix() < referenceDateObj.unix()) {
          return t('formValidation.minDate', {
            date: formatDate(referenceDate),
          });
        }
      }
    }

    return true;
  }

  function maxDate(value: string | Date, [referenceDate]: [Date | string]) {
    if (referenceDate && value) {
      const valueObj = dayjs(value);
      const referenceDateObj = dayjs(referenceDate);

      if (valueObj.isValid() && referenceDateObj.isValid()) {
        if (valueObj.unix() > referenceDateObj.unix()) {
          return t('formValidation.maxDate', {
            date: formatDate(referenceDate),
          });
        }
      }
    }

    return true;
  }

  return {
    required,
    email,
    maxArrayLength,
    minArrayLength,
    emailArray,
    phoneNumber,
    phoneMaxLength,
    maxLength,
    minDate,
    maxDate,
  };
}
